import * as React from 'react';
import { FileUpload } from 'primereact/fileupload';
import { useDispatch } from 'react-redux';

import { DataStorage } from '../../../../helpers/utils/DataStorage';
import { FileUploadValues } from '../../../../helpers/constants/systemValues/FileUploadValues';
import { FileRow, FileTableHeader, FileTableColumns } from '../../../../components/synced/ui/FileRow/FileRow';
import { EmptyState } from '../../../../components/synced/ui/EmptyState/EmptyState';
import { constantFormat } from '../../../../helpers/constants';
import { useIsMobile } from '../../../../helpers/hooks/useIsMobile';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { ContractorProjectDocFolderActions } from '../../../../helpers/constants/modelActions/ContractorProjectDocFolderActions';
import { ProjectDocFolderAttachment } from '../../models/ProjectDocFolderAttachment';
import { ProjectDoc } from '../../models/ProjectDoc';
import {
    ContractorViewerModal,
    isPdfAttachment,
    isWordAttachment
} from '../../../notes/components/viewer/PDFViewerModal';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../../helpers/hooks/useDownload';
import { alertSuccess } from '../../../../store/reducers/Alert';
import { handleError } from '../../../../helpers/hooks/handleError';
import { DocumentUploadMetadata, DocumentUploadMetadataDialog } from './DocumentUploadMetadataDialog';
import { ProjectDocTypeVals } from '../../../../helpers/mapping/ProjectDocTypes';
import { NoteUpdatesController } from '../../../notes/helpers/noteUpdates';

interface FileUploadComponentProps {
    actions: ModelAction[];
    projectDocFolderId: number;
    docTypeId?: number;
    folderTypeId?: number;
    contractorName?: string;
    onFilesLoaded: (count: number) => void;
    dataStorage: DataStorage;
    setDataStorageValue: (key: string, value: any) => void;
    uploadTypeId?: number;
    typeIdFilter?: number;
    readonly?: boolean;
    fetchFiles?: (folderId: number) => Promise<any>;
    onFilesChanged?: () => void;
    maxFiles?: number;
    // Нужны, чтобы открыть файл в просмотрщике замечаний и посчитать,
    // какие действия по замечанию доступны контрагенту.
    projectDocFolderStatusId?: number;
    projectDoc?: ProjectDoc;
    noteUpdates?: NoteUpdatesController;
    accept?: string;
    onInvalidFiles?: () => void;
    folderCypher?: string;
    folderDocumentOrganization?: string;
}

export const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
    actions,
    projectDocFolderId,
    docTypeId,
    folderTypeId,
    contractorName,
    onFilesLoaded,
    dataStorage,
    setDataStorageValue,
    uploadTypeId,
    typeIdFilter,
    readonly,
    fetchFiles,
    onFilesChanged,
    maxFiles,
    projectDocFolderStatusId,
    projectDoc,
    accept,
    onInvalidFiles,
    noteUpdates,
    folderCypher,
    folderDocumentOrganization
}) => {
    const dispatch = useDispatch();
    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
    const { downloadAttachment, isDownloading } = useDownload();
    // Файл, открытый в просмотрщике замечаний. Доступен, только если известен
    // статус папки — иначе нечем посчитать права контрагента на замечания.
    const [viewTarget, setViewTarget] = React.useState<ProjectDocFolderAttachment | null>(null);
    const canOpenViewer = projectDocFolderStatusId != null;
    const asJson = React.useMemo<boolean>(() => Boolean(new URLSearchParams(window.location.search).get('asjson')), []);
    const isMobile = useIsMobile(768);
    const fileColumns: FileTableColumns = isMobile
        ? { template: `1fr 60px`, showDate: false }
        : { template: `1fr 160px 110px`, showDate: true };

    const folderAttachmentsKey: string = `FileUploadComponent_folderAttachments`;
    const [folderAttachments, setFolderAttachments] = React.useState<ProjectDocFolderAttachment[]>(
        dataStorage.get<ProjectDocFolderAttachment[]>(folderAttachmentsKey, [])
    );

    const [fileStatuses, setFileStatuses] = React.useState<Map<string, 'pending' | 'uploading' | 'success' | 'error'>>(
        new Map()
    );

    const [currentProjectDocFolderId, setCurrentProjectDocFolderId] = React.useState<number>(projectDocFolderId);

    React.useEffect(() => {
        if (currentProjectDocFolderId === projectDocFolderId) return;

        // Сразу чистим список — иначе до ответа API видны файлы предыдущего раздела.
        setFolderAttachments([]);
        setDataStorageValue(folderAttachmentsKey, undefined);
        onFilesLoaded(0);
        setCurrentProjectDocFolderId(projectDocFolderId);
        // eslint-disable-next-line
    }, [projectDocFolderId]);

    const requestIdRef = React.useRef(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadfiles = React.useCallback(
        (force: boolean = true) => {
            const requestId = ++requestIdRef.current;

            if (!force && dataStorage.hasValue(folderAttachmentsKey)) {
                onFilesLoaded(folderAttachments.length);
                return;
            }

            if (currentProjectDocFolderId === 0) {
                setFolderAttachments([]);
                setDataStorageValue(folderAttachmentsKey, []);
                return;
            }

            promiseWrapper(
                () =>
                    fetchFiles
                        ? fetchFiles(currentProjectDocFolderId)
                        : services.pDFAttachmentsService.currentInstanceFetch(currentProjectDocFolderId),
                async (response) => {
                    const attachments = Array.isArray(response) ? response : response?.data || [];
                    const safeAttachments = attachments ?? [];

                    if (requestId !== requestIdRef.current) return;

                    if (justUploadedRef.current && safeAttachments.length === 0) {
                        console.warn('Empty after upload — retrying in 1000ms...');
                        await new Promise((r) => setTimeout(r, 1000));

                        try {
                            const retryRes =
                                await services.pDFAttachmentsService.currentInstanceFetch(currentProjectDocFolderId);
                            const retryAttachments = Array.isArray(retryRes.data) ? retryRes.data : [];

                            if (requestId !== requestIdRef.current) return;

                            setFolderAttachments(retryAttachments);
                            setDataStorageValue(folderAttachmentsKey, retryAttachments);
                            onFilesLoaded(retryAttachments.length);
                        } catch (error) {
                            console.error('Retry failed:', error);
                            setFolderAttachments([]);
                            setDataStorageValue(folderAttachmentsKey, []);
                            onFilesLoaded(0);
                        }
                        justUploadedRef.current = false;
                        return;
                    }

                    setFolderAttachments(safeAttachments);
                    setDataStorageValue(folderAttachmentsKey, safeAttachments);
                    onFilesLoaded(safeAttachments.length);
                    justUploadedRef.current = false;
                }
            );
        },
        [
            currentProjectDocFolderId,
            dataStorage,
            folderAttachmentsKey,
            setDataStorageValue,
            onFilesLoaded,
            folderAttachments.length,
            fetchFiles,
            promiseWrapper
        ]
    );

    // eslint-disable-next-line
    React.useEffect(loadfiles, [currentProjectDocFolderId]);

    const [uploadEnabled, setUploadEnabled] = React.useState<boolean>(false);
    const [deleteEnabled, setDeleteEnabled] = React.useState<boolean>(false);

    React.useEffect(() => {
        let hasUpload = false;
        let hasDelete = false;
        actions.forEach((action) => {
            switch (action.Id) {
                case ContractorProjectDocFolderActions.UploadAttachment:
                    hasUpload = true;
                    break;

                case ContractorProjectDocFolderActions.DeleteAttachment:
                    hasDelete = true;
                    break;
            }
        });

        setUploadEnabled(hasUpload);
        setDeleteEnabled(hasDelete);
    }, [actions]);

    const fileUploadRef = React.useRef<FileUpload>(null);
    const justUploadedRef = React.useRef(false);
    const pendingFilesRef = React.useRef<File[]>([]);
    const [metadataDialogVisible, setMetadataDialogVisible] = React.useState(false);

    // переехало в стандартное окно добавления документа
    const requiresUploadMetadata = false; //docTypeId === ProjectDocTypeVals.Executive;

    const performUpload = React.useCallback(
        (files: File[], metadata?: DocumentUploadMetadata) => {
            const uploadService = asJson ? services.pDFAttachmentsJsonService : services.pDFAttachmentsService;

            justUploadedRef.current = true;
            const initialStatuses = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
            files.forEach((file) => {
                initialStatuses.set(file.name, 'pending');
            });
            setFileStatuses(initialStatuses);

            const uploadTracker = {
                total: files.length,
                completed: 0,
                succeeded: 0,
                failed: 0
            };

            const checkCompletion = () => {
                if (uploadTracker.completed === uploadTracker.total) {
                    setTimeout(() => {
                        fileUploadRef.current?.clear();
                        setFileStatuses(new Map());
                    }, 1500);

                    if (uploadTracker.succeeded > 1) {
                        dispatch(
                            alertSuccess({
                                content: `${uploadTracker.succeeded} файл(а/ов) загружен(ы) успешно`,
                                closable: true
                            })
                        );
                    }

                    if (uploadTracker.failed > 0) {
                        dispatch({
                            type: 'ALERT_WARNING',
                            payload: {
                                content: `${uploadTracker.failed} файл(а/ов) не удалось загрузить`,
                                closable: true
                            }
                        });
                    }

                    loadfiles(true);
                    if (uploadTracker.succeeded > 0) {
                        onFilesChanged?.();
                    }
                }
            };

            const uploadSequentially = async () => {
                for (const file of files) {
                    setFileStatuses((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(file.name, 'uploading');
                        return newMap;
                    });

                    try {
                        await uploadService.uploadFiles(
                            projectDocFolderId,
                            [file],
                            metadata
                                ? {
                                      cypher: metadata.cypher,
                                      documentOrganization: metadata.documentOrganization
                                  }
                                : undefined,
                            uploadTypeId
                        );

                        uploadTracker.completed++;
                        uploadTracker.succeeded++;

                        setFileStatuses((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(file.name, 'success');
                            return newMap;
                        });

                        dispatch(
                            alertSuccess({
                                content: `Файл "${file.name}" загружен успешно`,
                                closable: true
                            })
                        );
                    } catch (error) {
                        uploadTracker.completed++;
                        uploadTracker.failed++;

                        setFileStatuses((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(file.name, 'error');
                            return newMap;
                        });

                        console.error(`Error uploading ${file.name}:`, error);
                        handleError(error, dispatch);
                    }
                }

                checkCompletion();
            };

            uploadSequentially();
        },
        [asJson, projectDocFolderId, loadfiles, dispatch]
    );

    const fileUploadHandler = React.useCallback(
        (event: any) => {
            const files = event.files as File[];
            if (files.length === 0) return;

            if (accept) {
                const invalid = files.filter((f) => !f.name.toLowerCase().endsWith('.pdf'));
                if (invalid.length > 0) {
                    onInvalidFiles?.();
                    fileUploadRef.current?.clear();
                    return;
                }
            }

            const filesToUpload = maxFiles ? files.slice(0, maxFiles) : files;

            if (requiresUploadMetadata) {
                pendingFilesRef.current = filesToUpload;
                setMetadataDialogVisible(true);
                return;
            }

            if (docTypeId === ProjectDocTypeVals.Executive && folderCypher) {
                performUpload(filesToUpload, {
                    cypher: folderCypher,
                    documentOrganization: folderDocumentOrganization ?? ''
                });
            } else {
                performUpload(filesToUpload);
            }
        },
        [
            requiresUploadMetadata,
            performUpload,
            maxFiles,
            accept,
            folderCypher,
            folderDocumentOrganization,
            onInvalidFiles
        ]
    );

    const onMetadataSave = React.useCallback(
        (metadata: DocumentUploadMetadata) => {
            setMetadataDialogVisible(false);
            performUpload(pendingFilesRef.current, metadata);
            pendingFilesRef.current = [];
        },
        [performUpload]
    );

    const onMetadataCancel = React.useCallback(() => {
        setMetadataDialogVisible(false);
        pendingFilesRef.current = [];
        fileUploadRef.current?.clear();
    }, []);

    const fileDeleteHandler = (data: ProjectDocFolderAttachment) => {
        promiseWrapper(
            () => services.pDFAttachmentsService.delete([data]),
            () => {
                loadfiles(true);
                onFilesChanged?.();
            }
        );
    };

    const fileDownloadHandler = (attachmentId: string) => {
        void downloadAttachment(attachmentId);
    };

    const emptyTemplate = (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: 'var(--text-muted)',
                fontSize: 14,
                padding: '16px 0',
                cursor: 'pointer',
                userSelect: 'none'
            }}
            onClick={() => (fileUploadRef.current as any)?.getInput()?.click()}
        >
            <i className="fa-solid fa-arrow-up-from-bracket" />
            {FileUploadValues.emptyMessage}
        </div>
    );

    const itemTemplate = (file: any, props: any) => {
        const status = fileStatuses.get(file.name) || 'pending';
        return (
            <div className="p-fileupload-row">
                <span
                    style={{
                        flex: 1,
                        fontSize: 13.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {file.name}
                </span>
                {status === 'uploading' && (
                    <span className="fu-badge fu-badge-uploading">
                        <i className="fa-solid fa-spinner fa-spin" />
                        Загрузка…
                    </span>
                )}
                {status === 'success' && (
                    <span className="fu-badge fu-badge-success">
                        <i className="fa-solid fa-check" />
                        Загружено
                    </span>
                )}
                {status === 'error' && (
                    <span className="fu-badge fu-badge-error">
                        <i className="fa-solid fa-xmark" />
                        Ошибка
                    </span>
                )}
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 'none' }}>{props.formatSize}</span>
                {status === 'pending' && (
                    <button type="button" className="fu-remove-btn" title="Убрать" onClick={props.onRemove}>
                        <i className="fa-solid fa-xmark" />
                    </button>
                )}
            </div>
        );
    };

    const displayAttachments =
        typeIdFilter != null ? folderAttachments.filter((a) => a.TypeId === typeIdFilter) : folderAttachments;

    const filesLoading = isLoading || (currentProjectDocFolderId > 0 && !dataStorage.hasValue(folderAttachmentsKey));

    return (
        <div className="row">
            {viewTarget && projectDocFolderStatusId != null && (
                <ContractorViewerModal
                    attachment={viewTarget}
                    projectDocFolderId={projectDocFolderId}
                    projectDocFolderStatusId={projectDocFolderStatusId}
                    projectDoc={projectDoc}
                    onHide={() => setViewTarget(null)}
                    onNotesChanged={onFilesChanged}
                    {...(noteUpdates ? { noteUpdates } : {})}
                />
            )}
            {requiresUploadMetadata && (
                <DocumentUploadMetadataDialog
                    visible={metadataDialogVisible}
                    folderTypeId={folderTypeId}
                    defaultOrganization={contractorName}
                    onSave={onMetadataSave}
                    onCancel={onMetadataCancel}
                />
            )}
            <div className="col-12 my-1">
                {uploadEnabled && !readonly && (!maxFiles || displayAttachments.length < maxFiles) && (
                    <FileUpload
                        multiple={!maxFiles || maxFiles > 1}
                        disabled={filesLoading}
                        ref={fileUploadRef}
                        name="uploadFiles[]"
                        maxFileSize={FileUploadValues.maxFileSize}
                        invalidFileSizeMessageSummary={FileUploadValues.invalidFileSizeMessageSummary}
                        invalidFileSizeMessageDetail={FileUploadValues.invalidFileSizeMessageDetail}
                        emptyTemplate={emptyTemplate}
                        itemTemplate={itemTemplate}
                        customUpload
                        uploadHandler={fileUploadHandler}
                        chooseOptions={{ icon: 'fa-solid fa-plus', label: FileUploadValues.chooseLabel }}
                        uploadOptions={{ icon: 'fa-solid fa-cloud-arrow-up', label: FileUploadValues.uploadLabel }}
                        cancelOptions={{ icon: 'fa-solid fa-xmark', label: FileUploadValues.cancelLabel }}
                        accept={accept || FileUploadValues.accept}
                    />
                )}
            </div>
            <div className={`col-12 block-loading-host${filesLoading ? ` block-loading-host--loading` : ``}`}>
                {filesLoading && (
                    <div className="block-loading-overlay" aria-busy="true" aria-live="polite">
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        <span>Загрузка…</span>
                    </div>
                )}
                {displayAttachments?.length > 0 ? (
                    <div className="files-table">
                        <FileTableHeader columns={fileColumns} />
                        {displayAttachments.map((att) => {
                            const title = att.Attachment?.Title ?? att.Attachment?.FileName ?? '';
                            const created = att.Attachment?.Created;
                            const dateStr = created
                                ? constantFormat.DateToString(constantFormat.StringToDate(created as unknown as string))
                                : '';
                            // PDF/Word с известным статусом папки открываются во вьювере замечаний
                            // по клику на строку; скачивание для них — отдельная явная кнопка.
                            const canView = canOpenViewer && (isPdfAttachment(att) || isWordAttachment(att));
                            return (
                                <FileRow
                                    key={att.Id}
                                    columns={fileColumns}
                                    name={title}
                                    extSource={att.Attachment?.FileName ?? title}
                                    date={dateStr}
                                    onClick={
                                        canView ? () => setViewTarget(att) : () => fileDownloadHandler(att.AttachmentId)
                                    }
                                    actionsSlot={
                                        <>
                                            <button
                                                type="button"
                                                title={isDownloading ? `Скачивание…` : `Скачать`}
                                                className="files-delete-btn"
                                                disabled={isDownloading}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fileDownloadHandler(att.AttachmentId);
                                                }}
                                            >
                                                <i
                                                    className={
                                                        isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`
                                                    }
                                                />
                                            </button>
                                            {deleteEnabled && (
                                                <button
                                                    type="button"
                                                    title="Удалить"
                                                    className="files-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        fileDeleteHandler(att);
                                                    }}
                                                >
                                                    <i className="fa-regular fa-trash-can" />
                                                </button>
                                            )}
                                        </>
                                    }
                                />
                            );
                        })}
                    </div>
                ) : (
                    !filesLoading &&
                    !uploadEnabled && <EmptyState icon="fa-folder-open" title="В этом разделе пока нет файлов" />
                )}
                {filesLoading && displayAttachments.length === 0 && (
                    <div className="files-loading-empty" aria-hidden="true" />
                )}
            </div>
        </div>
    );
};
